<script setup lang="ts">
import { Ellipsis, LogOut } from "@lucide/vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute, useRouter } from "vue-router";

import { useSessionStore } from "@/entities/session";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui";

import { visibleNavZones } from "./model/navigation";

/**
 * Нижня таб-панель адмінки — мобільний еквівалент sidebar (IA D2, частотний
 * поділ). Показується лише на mobile (`md:hidden`); десктоп далі тримає sidebar.
 * 3 щоденні таби (Журнал · Майстри · Клієнти) + «Ще», що відкриває bottom-Sheet
 * зі зоною «Налаштування салону» та виходом.
 *
 * Джерело правди nav-конфігу — `visibleNavZones(canManageSalon)` (той самий seam,
 * що й sidebar): дублювати список роутів НЕ можна. Коли feature-021 (RBAC) дасть
 * `canManageSalon=false`, зона налаштувань зникне з листа без правок тут.
 *
 * Logout-редірект у widget (FSD: `entities` не знає про router, ADR-0006 D4).
 * Дубль виходу з top-bar-меню — навмисний (mobile-доступність).
 */
const props = withDefaults(defineProps<{ canManageSalon?: boolean }>(), {
  canManageSalon: true,
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const session = useSessionStore();

const zones = computed(() => visibleNavZones(props.canManageSalon));
const dailyItems = computed(() => zones.value.find((z) => z.id === "daily")?.items ?? []);
const settingsItems = computed(() => zones.value.find((z) => z.id === "settings")?.items ?? []);

const moreOpen = ref(false);

/**
 * «Ще» активний, коли відкрито лист АБО поточний роут належить зоні налаштувань
 * (того табу на панелі немає, тож його активність відбиваємо на «Ще»).
 */
const moreActive = computed(
  () =>
    moreOpen.value
    || settingsItems.value.some(
      (item) => route.path === item.to || route.path.startsWith(`${item.to}/`),
    ),
);

/**
 * `navigate` отримує click-event, інакше vue-router не може `preventDefault()`
 * нативний перехід `<a href>` → відбувається hard-reload поряд з `router.push`.
 * Лист закриваємо ПІСЛЯ навігації.
 */
function goToSettings(e: MouseEvent, navigate: (e?: MouseEvent) => void): void {
  navigate(e);
  moreOpen.value = false;
}

async function onLogout(): Promise<void> {
  moreOpen.value = false;
  await session.logout();
  await router.push("/login");
}
</script>

<template>
  <nav
    data-testid="bottom-nav"
    class="fixed inset-x-0 bottom-0 z-40 flex min-h-[var(--bottomnav-height)] border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
    :aria-label="t('layout.aria.bottomnav')"
  >
    <RouterLink
      v-for="item in dailyItems"
      :key="item.name"
      v-slot="{ href, navigate, isActive, isExactActive }"
      :to="item.to"
      custom
    >
      <a
        :href="href"
        data-slot="bottomnav-item"
        :aria-current="(item.exact ? isExactActive : isActive) ? 'page' : undefined"
        :class="[
          'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs font-medium outline-none transition-colors',
          'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          (item.exact ? isExactActive : isActive)
            ? 'text-primary before:absolute before:inset-x-3 before:top-0 before:h-0.5 before:bg-primary before:rounded-b'
            : 'text-muted-foreground hover:text-foreground',
        ]"
        @click="navigate"
      >
        <component
          :is="item.icon"
          class="size-5 shrink-0"
          aria-hidden="true"
        />
        <span>{{ t(item.labelKey) }}</span>
      </a>
    </RouterLink>

    <button
      type="button"
      data-slot="bottomnav-more"
      :data-active="moreActive ? 'true' : undefined"
      :aria-expanded="moreOpen"
      aria-haspopup="dialog"
      :class="[
        'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs font-medium outline-none transition-colors',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        moreActive
          ? 'text-primary before:absolute before:inset-x-3 before:top-0 before:h-0.5 before:bg-primary before:rounded-b'
          : 'text-muted-foreground hover:text-foreground',
      ]"
      @click="moreOpen = true"
    >
      <Ellipsis
        class="size-5 shrink-0"
        aria-hidden="true"
      />
      <span>{{ t("nav.more") }}</span>
    </button>
  </nav>

  <Sheet v-model:open="moreOpen">
    <SheetContent :close-aria-label="t('layout.more.close')">
      <SheetHeader>
        <SheetTitle>{{ t("layout.more.title") }}</SheetTitle>
        <SheetDescription>{{ t("layout.more.description") }}</SheetDescription>
      </SheetHeader>

      <nav
        v-if="settingsItems.length"
        :aria-label="t('layout.aria.moreSections')"
        class="flex flex-col gap-1 py-2"
      >
        <RouterLink
          v-for="item in settingsItems"
          :key="item.name"
          v-slot="{ href, navigate, isActive }"
          :to="item.to"
          custom
        >
          <a
            :href="href"
            data-slot="bottomnav-sheet-item"
            :aria-current="isActive ? 'page' : undefined"
            :class="[
              'flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium outline-none transition-colors',
              'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-foreground hover:bg-accent/50',
            ]"
            @click="goToSettings($event, navigate)"
          >
            <component
              :is="item.icon"
              class="size-5 shrink-0"
              aria-hidden="true"
            />
            <span>{{ t(item.labelKey) }}</span>
          </a>
        </RouterLink>
      </nav>

      <SheetFooter>
        <button
          type="button"
          data-testid="bottomnav-logout"
          class="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-sm font-medium text-destructive outline-none transition-colors hover:bg-destructive/10 focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          @click="onLogout"
        >
          <LogOut
            class="size-5 shrink-0"
            aria-hidden="true"
          />
          <span>{{ t("layout.account.logout") }}</span>
        </button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
